import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { app } from "@microsoft/teams-js";
import { getTeamsSsoToken, withTimeout } from "./teamsSso";
import { SoftphoneSipConfig, UserPermissions } from "./useSoftphoneConfig";
import { useTeamsUser } from "./useTeamsUser";
import { LocalizedMessage, TranslationKey } from "../i18n/LocaleContext";
import { DEFAULT_LOCALE, SupportedLocale } from "../i18n/locale";
import { getRuntimeEnv } from "../config/runtimeEnv";
import { debugLog } from "../config/debug";
import { setSender as setClientLogSender } from "../debug/clientLog";
import { getEntityId, getSessionIdentity, getTeamsAppId, querystring, sessionId, setHostInfo } from "../config/session";

const KNOWN_TIMEOUT_MESSAGES: Record<string, TranslationKey> = {
  "Timed out while initializing the Teams host.": "bootstrap.error.timeoutInitHost",
  "Timed out while getting Teams SSO token.": "bootstrap.error.timeoutSsoToken",
  "Timed out while loading Teams host context.": "bootstrap.error.timeoutHostContext",
};

// Fallback deliberadamente genérico: a string original do erro pode conter a URL da
// requisição (e, no caso do PABX, o token na query string), então ela não vai para a tela.
function toLocalizedMessage(message: string): LocalizedMessage {
  const key = KNOWN_TIMEOUT_MESSAGES[message];
  return key ? { key } : { key: "bootstrap.error.unexpected" };
}

const EMPTY_DETAIL: LocalizedMessage = { key: "common.none" };

// notFound é um enum do backend; traduzir em vez de exibir o identificador cru.
const NOT_FOUND_MESSAGES: Record<string, TranslationKey> = {
  tenant: "bootstrap.notFound.tenant",
  user: "bootstrap.notFound.user",
  extension: "bootstrap.notFound.extension",
};

// Referência curta e sem segredos para o suporte cruzar com o log do backend.
function buildErrorCode(status: number | "NET"): string {
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return `${status} · ${time}`;
}

// Do corpo de erro só aproveitamos o campo `message`; o corpo cru (JSON, HTML de proxy,
// texto de erro do fetch) nunca é exibido.
function extractBackendMessage(responseText: string): string {
  try {
    const parsed = JSON.parse(responseText) as { message?: unknown };
    return typeof parsed.message === "string" ? parsed.message.trim() : "";
  } catch {
    return "";
  }
}

type SoftphoneCheckInPayload = {
  userId: string;
  displayName: string;
  loginHint: string;
  tenantId: string;
};

type UserCheckInData = {
  pabxApiToken: string;
  sipUserConfig: SoftphoneSipConfig;
  // Ausente em backends anteriores ao campo; ver fallback no branch 200.
  permissions?: UserPermissions;
  // Intervalo do heartbeat da sessão (useSessionPoll); ausente em backends anteriores às sessões.
  pollIntervalSeconds?: number;
};

// Corpo dos check-ins que NÃO entregam configuração (402, 404). O backend inclui `tenantAdmin`
// apenas nos dois bloqueios que o próprio administrador resolve — ramal não configurado e licença
// ausente —, e é o que permite oferecer-lhe a tela de administração na própria tela de bloqueio
// (ver BlockedStateScreen em App.tsx). Os bloqueios de origem uTech não o trazem.
type BlockedCheckInData = {
  tenantAdmin?: boolean;
  notFound?: string;
};

export const DEFAULT_USER_PERMISSIONS: UserPermissions = {
  recordAccess: true,
  pabxContactsAccess: true,
  tenantAdmin: false,
};

type SoftphoneConfigBootstrapState = {
  status: "loading" | "ready" | "error";
  sip: SoftphoneSipConfig | null;
  pabxApiToken: string | null;
  permissions: UserPermissions | null;
  // null = backend sem sessões: o heartbeat fica desligado.
  pollIntervalSeconds: number | null;
  lastUpdatedAt: number;
};

// Resposta de POST /api/bearer/sessionPoll (msteams-service). `sipUserConfig` vem sem a senha.
export type SessionPollResult = {
  sessionKnown: boolean;
  tenantStatus: string | null;
  licenced: boolean;
  permissions: Partial<UserPermissions> | null;
  sipUserConfig: Partial<Omit<SoftphoneSipConfig, "password">> | null;
};

export type TeamsSsoState = {
  status: "loading" | "ready" | "error" | "unavailable";
  detail: LocalizedMessage;
  // Texto do erro do Teams SDK (host de autenticação), exibido apenas no bloco recolhido de
  // diagnóstico. Não vem do nosso backend nem do PABX, então não carrega o token.
  errorDetail?: string;
};

export type TeamsHostDiagnostics = {
  hostName: string;
  clientType: string;
  frameContext: string;
  pageUrl: string;
  expectedWebApplicationId: string;
  expectedWebApplicationResource: string;
  suggestedAuthorizedClientAppId: string;
  ssoClientAppId: string;
  contextError: string;
};

export type RegistrationStatus =
  | "idle"
  | "waiting-softphone-config"
  | "syncing"
  | "synced"
  | "onboarding-required"
  | "user-not-configured"
  | "pending-approval"
  | "access-blocked"
  | "license-required"
  | "permissions-required"
  | "sync-error";

export const UNAVAILABLE_SENTINEL = "__unavailable__";

const TEAMS_WEB_APPLICATION_ID = getRuntimeEnv("REACT_APP_TEAMS_WEB_APPLICATION_ID") || "";
const TEAMS_WEB_CLIENT_APP_ID = getRuntimeEnv("REACT_APP_TEAMS_WEB_CLIENT_APP_ID") || "";
const TEAMS_DESKTOP_CLIENT_APP_ID = getRuntimeEnv("REACT_APP_TEAMS_DESKTOP_CLIENT_APP_ID") || "";

function buildExpectedWebApplicationResource(): string {
  if (typeof window === "undefined") {
    return `api://<host>/${TEAMS_WEB_APPLICATION_ID}`;
  }

  return `api://${window.location.host}/${TEAMS_WEB_APPLICATION_ID}`;
}

export function buildApiEndpoint(path: string): string {
  const baseUrl = (getRuntimeEnv("REACT_APP_TEAMS_BOT_API_BASE_URL") || "").trim();
  if (baseUrl) {
    return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  }

  if (typeof window === "undefined") {
    return `/${path.replace(/^\/+/, "")}`;
  }

  return `${window.location.origin}/${path.replace(/^\/+/, "")}`;
}

function normalizeErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function extractClientAppId(errorMessage: string): string {
  const match = errorMessage.match(/Client app ID:\s*([0-9a-fA-F-]{36})/i);
  return match?.[1] || UNAVAILABLE_SENTINEL;
}

function inferSuggestedAuthorizedClientAppId(clientType: string): string {
  if (clientType === "desktop") {
    return TEAMS_DESKTOP_CLIENT_APP_ID;
  }

  if (clientType === "web") {
    return TEAMS_WEB_CLIENT_APP_ID;
  }

  return UNAVAILABLE_SENTINEL;
}

export function useAppBootstrapFlow({
  enabled = true,
  locale,
  localeStatus,
}: {
  enabled?: boolean;
  locale: SupportedLocale;
  localeStatus: "loading" | "ready" | "fallback";
} = { locale: DEFAULT_LOCALE, localeStatus: "loading" }) {
  const teamsUser = useTeamsUser();
  const teamsSsoAccessTokenRef = useRef<string | null>(null);
  const lastSyncedUserDataRef = useRef<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [softphoneConfig, setSoftphoneConfig] = useState<SoftphoneConfigBootstrapState>({
    status: "loading",
    sip: null,
    pabxApiToken: null,
    permissions: null,
    pollIntervalSeconds: null,
    lastUpdatedAt: Date.now(),
  });
  const [teamsSsoState, setTeamsSsoState] = useState<TeamsSsoState>({
    status: "loading",
    detail: { key: "bootstrap.ssoState.validatingAuth" },
  });
  const [teamsHostDiagnostics, setTeamsHostDiagnostics] = useState<TeamsHostDiagnostics>({
    hostName: UNAVAILABLE_SENTINEL,
    clientType: UNAVAILABLE_SENTINEL,
    frameContext: UNAVAILABLE_SENTINEL,
    pageUrl: typeof window !== "undefined" ? window.location.href : UNAVAILABLE_SENTINEL,
    expectedWebApplicationId: TEAMS_WEB_APPLICATION_ID,
    expectedWebApplicationResource: buildExpectedWebApplicationResource(),
    suggestedAuthorizedClientAppId: UNAVAILABLE_SENTINEL,
    ssoClientAppId: UNAVAILABLE_SENTINEL,
    contextError: "",
  });
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus>("idle");
  const [registrationDetail, setRegistrationDetail] = useState<LocalizedMessage>(EMPTY_DETAIL);
  // Mensagem que o backend devolve no campo `message` do corpo de erro. É o único texto de
  // origem remota exibido na tela — o backend não coloca detalhes internos aí (ver
  // msteams-service/src/app/api-router.js).
  const [registrationBackendMessage, setRegistrationBackendMessage] = useState("");
  const [registrationErrorCode, setRegistrationErrorCode] = useState("");
  // Usuário é administrador do tenant: no check-in 200 vem em `permissions.tenantAdmin`; nos
  // bloqueios que ele resolve (402, 404 de usuário/ramal) vem no campo `tenantAdmin` do corpo.
  // Nos dois casos é o que libera a tela de administração de usuários.
  const [tenantAdminAccess, setTenantAdminAccess] = useState(false);

  const userCheckInEndpoint = useMemo(() => buildApiEndpoint("/api/bearer/userCheckIn"), []);
  const appEventNotificationEndpoint = useMemo(() => buildApiEndpoint("/api/bearer/appEventNotification"), []);
  const appVisibilityEndpoint = useMemo(() => buildApiEndpoint("/api/bearer/appVisibility"), []);
  const sessionPollEndpoint = useMemo(() => buildApiEndpoint("/api/bearer/sessionPoll"), []);

  /**
   * O token do Teams vale cerca de uma hora. O cache aqui evita um round-trip ao host a cada
   * requisição, mas não sabe quando o token venceu — quem sabe é o host, que o renova sozinho.
   * Por isso `forceRefresh`: ao tomar 401, descartamos o valor guardado e pedimos outro. Não é
   * reautenticação do usuário, é só parar de reusar um valor vencido.
   */
  const resolveTeamsSsoToken = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && teamsSsoAccessTokenRef.current) {
      return teamsSsoAccessTokenRef.current;
    }

    const accessToken = await getTeamsSsoToken();
    teamsSsoAccessTokenRef.current = accessToken;
    return accessToken;
  }, []);

  /**
   * Requisição autenticada com renovação reativa: um 401 é tratado como token vencido, o cache é
   * descartado e a requisição repetida **uma** vez. Sem isto, passada a validade do token toda
   * chamada falha em silêncio até a página ser recarregada — presença, sinal de visibilidade e
   * log de console param juntos, sem nada indicar o motivo.
   *
   * `body` é opcional porque a tela de administração do tenant também faz GETs por aqui, e o
   * navegador rejeita (TypeError) um GET com corpo, mesmo vazio.
   */
  const authorizedFetch = useCallback(
    async (url: string, init: RequestInit, body?: string) => {
      const send = async (accessToken: string) =>
        fetch(url, {
          ...init,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
          ...(body === undefined ? {} : { body }),
        });

      const response = await send(await resolveTeamsSsoToken());
      if (response.status !== 401) return response;

      return send(await resolveTeamsSsoToken(true));
    },
    [resolveTeamsSsoToken]
  );

  const userCheckInPayload = useMemo<SoftphoneCheckInPayload | null>(() => {
    if (teamsUser.status !== "ready" || !teamsUser.user?.userId) return null;

    return {
      userId: teamsUser.user.userId,
      displayName: teamsUser.user.displayName || "",
      loginHint: teamsUser.user.loginHint || "",
      tenantId: teamsUser.user.tenantId || "",
    };
  }, [teamsUser]);

  const refresh = useCallback(() => {
    lastSyncedUserDataRef.current = null;
    setSoftphoneConfig({
      status: "loading",
      sip: null,
      pabxApiToken: null,
      permissions: null,
      pollIntervalSeconds: null,
      lastUpdatedAt: Date.now(),
    });
    setRegistrationStatus("idle");
    setRegistrationDetail(EMPTY_DETAIL);
    setRegistrationBackendMessage("");
    setRegistrationErrorCode("");
    // O efeito do SSO volta a "loading" ao ver o nonce novo, mas só no render seguinte; sem isto,
    // o efeito do check-in roda no mesmo commit ainda vendo "ready", dispara, e a volta a "loading"
    // descarta a resposta (cleanup) com a chave de idempotência já gravada — tela presa em "idle".
    // No navegador o token em cache costuma resolver antes desse render e esconde a corrida; aqui
    // ela deixa de depender do agendador: o check-in só sai quando o SSO voltar a "ready".
    setTeamsSsoState({ status: "loading", detail: { key: "bootstrap.ssoState.gettingSsoToken" } });
    setRefreshNonce((current) => current + 1);
  }, []);

  useEffect(() => {
    let disposed = false;

    async function loadHostDiagnostics() {
      try {
        await withTimeout(app.initialize(), 1500, "Timed out while initializing the Teams host.");
        const context = await withTimeout(app.getContext(), 1500, "Timed out while loading Teams host context.");

        if (disposed) return;

        // Publica o cliente no <html> para a CSS decidir a safe area, no mesmo padrão do
        // data-app-theme de useHostTheme. Medido no Teams Android (Galaxy S23 FE): o
        // webview já vem recuado acima da barra de gestos, mas env(safe-area-inset-bottom)
        // continua reportando 48px — reservá-los desperdiçaria 48 de 690px de altura útil.
        // No iOS a reserva é legítima (indicador de home), então só ela é aplicada.
        document.documentElement.dataset.hostClient = context.app.host.clientType || "";
        // Rótulo da sessão para o backend/msteams-ui; fora do payload do check-in de propósito
        // (ver src/config/session.ts).
        setHostInfo({
          clientType: context.app.host.clientType,
          hostName: context.app.host.name,
          teamsAppId: context.app.appId?.toString(),
          entityId: context.page.id,
        });

        setTeamsHostDiagnostics((current) => ({
          ...current,
          hostName: context.app.host.name || UNAVAILABLE_SENTINEL,
          clientType: context.app.host.clientType || UNAVAILABLE_SENTINEL,
          frameContext: String(context.page.frameContext || UNAVAILABLE_SENTINEL),
          pageUrl: typeof window !== "undefined" ? window.location.href : current.pageUrl,
          suggestedAuthorizedClientAppId: inferSuggestedAuthorizedClientAppId(context.app.host.clientType || ""),
          contextError: "",
        }));
      } catch (error) {
        if (disposed) return;

        setTeamsHostDiagnostics((current) => ({
          ...current,
          pageUrl: typeof window !== "undefined" ? window.location.href : current.pageUrl,
          contextError: error instanceof Error ? error.message : String(error),
        }));
      }
    }

    void loadHostDiagnostics();

    return () => {
      disposed = true;
    };
  }, []);

  useEffect(() => {
    let disposed = false;

    if (!enabled) {
      return () => {
        disposed = true;
      };
    }

    if (teamsUser.status === "loading") {
      setTeamsSsoState({
        status: "loading",
        detail: { key: "bootstrap.ssoState.waitingTeamsContext" },
      });
      return () => {
        disposed = true;
      };
    }

    if (teamsUser.status === "unavailable") {
      teamsSsoAccessTokenRef.current = null;
      setTeamsSsoState({
        status: "unavailable",
        detail: { key: "bootstrap.ssoState.openTeamsRequired" },
      });
      return () => {
        disposed = true;
      };
    }

    if (teamsUser.status !== "ready" || !teamsUser.user?.userId) {
      teamsSsoAccessTokenRef.current = null;
      setTeamsSsoState({
        status: "error",
        detail: { key: "bootstrap.ssoState.invalidTeamsUser" },
      });
      return () => {
        disposed = true;
      };
    }

    setTeamsSsoState({
      status: "loading",
      detail: { key: "bootstrap.ssoState.gettingSsoToken" },
    });

    void resolveTeamsSsoToken()
      .then((accessToken) => {
        if (disposed) return;

        setTeamsSsoState({
          status: "ready",
          detail: { key: "bootstrap.ssoState.tokenReady", params: { charCount: accessToken.length } },
        });
      })
      .catch((error) => {
        if (disposed) return;

        const errorMessage = normalizeErrorMessage(error);
        teamsSsoAccessTokenRef.current = null;
        setTeamsHostDiagnostics((current) => ({
          ...current,
          ssoClientAppId: extractClientAppId(errorMessage),
        }));
        setTeamsSsoState({
          status: "error",
          detail: toLocalizedMessage(errorMessage),
          errorDetail: errorMessage,
        });
      });

    return () => {
      disposed = true;
    };
  }, [enabled, resolveTeamsSsoToken, teamsUser, refreshNonce]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!userCheckInEndpoint) {
      setSoftphoneConfig({
        status: "error",
        sip: null,
        pabxApiToken: null,
        permissions: null,
        pollIntervalSeconds: null,
        lastUpdatedAt: Date.now(),
      });
      setRegistrationStatus("sync-error");
      setRegistrationDetail({ key: "bootstrap.registration.backendSyncDisabled" });
      return;
    }

    if (teamsSsoState.status !== "ready") {
      setRegistrationStatus("idle");
      setRegistrationDetail({ key: "bootstrap.registration.waitingSso", params: { status: teamsSsoState.status } });
      return;
    }

    if (!userCheckInPayload) {
      setRegistrationStatus("sync-error");
      setRegistrationDetail({ key: "bootstrap.registration.payloadNotReady" });
      return;
    }

    // O check-in espera o locale do Teams: enviá-lo antes de resolvido gravaria o padrão no
    // documento do usuário. O efeito reexecuta quando localeStatus sai de "loading".
    if (localeStatus === "loading") {
      return;
    }

    const requestKey = `${refreshNonce}:${JSON.stringify(userCheckInPayload)}`;
    if (lastSyncedUserDataRef.current === requestKey) {
      return;
    }

    // Tripwire: daqui para baixo o efeito zera o sip e troca a branch renderizada por App.tsx,
    // destruindo o iframe do softphone. Um resume nunca deve chegar aqui — se chegar, esta linha
    // aparece no log logo depois do [lifecycle] onResume e explica a chamada derrubada.
    console.log(`[lifecycle] checkIn disparado key=${requestKey}`);

    lastSyncedUserDataRef.current = requestKey;
    setSoftphoneConfig((current) => ({
      ...current,
      status: "loading",
      sip: null,
      lastUpdatedAt: Date.now(),
    }));
    setRegistrationStatus("syncing");
    setRegistrationDetail({ key: "bootstrap.registration.syncing" });
    setRegistrationBackendMessage("");
    setRegistrationErrorCode("");

    let disposed = false;

    void resolveTeamsSsoToken()
      .then((accessToken) =>
        fetch(userCheckInEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          // A identidade da sessão é lida na hora do envio, e não faz parte da chave de
          // idempotência do check-in (requestKey): clientType/hostName chegam de forma
          // assíncrona e não podem provocar um segundo check-in. O mesmo vale para o locale e
          // para teamsAppId/entityId/querystring (como o Teams abriu o app).
          body: JSON.stringify({
            ...userCheckInPayload,
            ...getSessionIdentity(),
            locale,
            teamsAppId: getTeamsAppId(),
            entityId: getEntityId(),
            querystring,
          }),
        })
      )
      .then(async (response) => {
        if (disposed) return;

        if (response.status === 202) {
          setSoftphoneConfig({
            status: "error",
            sip: null,
            pabxApiToken: null,
            permissions: null,
            pollIntervalSeconds: null,
            lastUpdatedAt: Date.now(),
          });
          setRegistrationStatus("pending-approval");
          setRegistrationDetail({ key: "bootstrap.registration.pendingApproval" });
          return;
        }

        if (response.status === 403) {
          setSoftphoneConfig({
            status: "error",
            sip: null,
            pabxApiToken: null,
            permissions: null,
            pollIntervalSeconds: null,
            lastUpdatedAt: Date.now(),
          });
          setRegistrationStatus("access-blocked");
          setRegistrationDetail({ key: "bootstrap.registration.accessBlocked" });
          return;
        }

        if (response.status === 402) {
          const body = (await response.json().catch(() => ({}))) as BlockedCheckInData;
          setSoftphoneConfig({
            status: "error",
            sip: null,
            pabxApiToken: null,
            permissions: null,
            pollIntervalSeconds: null,
            lastUpdatedAt: Date.now(),
          });
          setTenantAdminAccess(body.tenantAdmin === true);
          setRegistrationStatus("license-required");
          setRegistrationDetail({ key: "bootstrap.registration.licenseRequired" });
          return;
        }

        // 541: o tenant está em "permissions required" — o admin precisa refazer o consentimento.
        if (response.status === 541) {
          setSoftphoneConfig({
            status: "error",
            sip: null,
            pabxApiToken: null,
            permissions: null,
            pollIntervalSeconds: null,
            lastUpdatedAt: Date.now(),
          });
          setRegistrationStatus("permissions-required");
          setRegistrationDetail({ key: "bootstrap.registration.permissionsRequired" });
          return;
        }

        if (response.status === 404) {
          const body = await response.json() as { message?: string } & BlockedCheckInData;
          debugLog("userCheckIn response:", { status: response.status, body });
          if (body.notFound === "tenant") {
            setSoftphoneConfig({
              status: "error",
              sip: null,
              pabxApiToken: null,
              permissions: null,
              pollIntervalSeconds: null,
              lastUpdatedAt: Date.now(),
            });
            setRegistrationStatus("onboarding-required");
            setRegistrationDetail({ key: "bootstrap.registration.onboardingRequired" });
            return;
          }
          setSoftphoneConfig({
            status: "error",
            sip: null,
            pabxApiToken: null,
            permissions: null,
            pollIntervalSeconds: null,
            lastUpdatedAt: Date.now(),
          });
          setTenantAdminAccess(body.tenantAdmin === true);
          setRegistrationStatus("user-not-configured");
          setRegistrationDetail({ key: NOT_FOUND_MESSAGES[body.notFound || ""] || "common.unknown" });
          return;
        }

        if (!response.ok) {
          const responseText = await response.text();
          setSoftphoneConfig({
            status: "error",
            sip: null,
            pabxApiToken: null,
            permissions: null,
            pollIntervalSeconds: null,
            lastUpdatedAt: Date.now(),
          });
          setRegistrationStatus("sync-error");
          setRegistrationDetail({
            key: "bootstrap.registration.httpError",
            params: { status: response.status, statusText: response.statusText },
          });
          setRegistrationBackendMessage(extractBackendMessage(responseText));
          setRegistrationErrorCode(buildErrorCode(response.status));
          return;
        }

        const checkInData = (await response.json()) as UserCheckInData;

        setTenantAdminAccess(checkInData.permissions?.tenantAdmin === true);
        setSoftphoneConfig({
          status: "ready",
          sip: checkInData.sipUserConfig,
          pabxApiToken: checkInData.pabxApiToken,
          // Backend antigo sem o campo: mantém o comportamento permissivo anterior — menos
          // `tenantAdmin`, cuja omissão significa "não é admin" (ver DEFAULT_USER_PERMISSIONS).
          permissions: { ...DEFAULT_USER_PERMISSIONS, ...(checkInData.permissions ?? {}) },
          pollIntervalSeconds:
            typeof checkInData.pollIntervalSeconds === "number" && checkInData.pollIntervalSeconds > 0
              ? checkInData.pollIntervalSeconds
              : null,
          lastUpdatedAt: Date.now(),
        });

        setRegistrationStatus("synced");
        setRegistrationDetail({ key: "bootstrap.registration.synced" });
      })
      .catch((error) => {
        if (disposed) return;

        setRegistrationStatus("sync-error");
        setRegistrationDetail(toLocalizedMessage(normalizeErrorMessage(error)));
        setRegistrationBackendMessage("");
        setRegistrationErrorCode(buildErrorCode("NET"));
      });

    return () => {
      disposed = true;
    };
  }, [
    enabled,
    locale,
    localeStatus,
    refreshNonce,
    resolveTeamsSsoToken,
    teamsSsoState.status,
    userCheckInEndpoint,
    userCheckInPayload,
  ]);

  const sendAppEventNotification = useCallback(
    async (
      event: { type: string; payload?: unknown },
      signal?: AbortSignal,
      options: { keepalive?: boolean } = {}
    ) => {
      if (!userCheckInPayload) return;

      // O `signal` é usado pelo log de console, que serializa os envios: sem poder abortar, uma
      // requisição pendurada travaria a fila inteira. Cobre também o SSO, que pode pendurar antes
      // mesmo do fetch.
      //
      // `keepalive` é só para os eventos de ciclo de vida emitidos com o documento morrendo
      // (app.unload em pagehide). O log de console NÃO pode usá-lo: a cota de keepalive é global do
      // navegador (64 KB em voo) e uma rajada de log afogaria estes sinais e o de visibilidade.
      const response = await authorizedFetch(
        appEventNotificationEndpoint,
        { method: "POST", signal, keepalive: options.keepalive === true },
        JSON.stringify({ ...userCheckInPayload, sessionId, event })
      );

      // `fetch` só rejeita em erro de rede ou abort — 401, 413 e 500 resolvem normalmente. Sem
      // esta checagem o chamador trata erro do servidor como entrega feita, e o log de console
      // some sem gerar o marcador de falha que deveria tornar a perda visível.
      if (!response.ok) {
        throw new Error(`appEventNotification respondeu ${response.status}`);
      }
    },
    [appEventNotificationEndpoint, authorizedFetch, userCheckInPayload]
  );

  // O módulo de log de console precisa de um transporte autenticado; é o mesmo POST usado
  // pelos demais eventos, e só funciona depois de haver identidade e token.
  useEffect(() => {
    setClientLogSender(sendAppEventNotification);
  }, [sendAppEventNotification]);

  const lastSentVisibilityRef = useRef<boolean | null>(null);

  const sendAppVisibility = useCallback(
    async (visible: boolean) => {
      // As três linhas abaixo são o que torna o sinal legível no log remoto: sem elas não dá para
      // distinguir "não enviei porque já estava nesse estado" de "não enviei porque ainda não há
      // identidade" — e é justamente essa diferença que explica um usuário preso em invisível.
      if (!userCheckInPayload) {
        console.log(`[visibility] -> ${visible} (sem payload)`);
        return;
      }
      if (lastSentVisibilityRef.current === visible) {
        console.log(`[visibility] -> ${visible} (dedupe)`);
        return;
      }
      lastSentVisibilityRef.current = visible;
      console.log(`[visibility] -> ${visible} (enviado)`);

      try {
        const response = await authorizedFetch(
          appVisibilityEndpoint,
          {
            method: "POST",
            // Sinais de "invisible" costumam disparar quando a aba/janela está sendo fechada;
            // sem keepalive o browser cancela o fetch junto com o unload do documento.
            keepalive: true,
          },
          JSON.stringify({
            tenantId: userCheckInPayload.tenantId,
            userId: userCheckInPayload.userId,
            sessionId,
            visible,
          })
        );
        if (!response.ok) {
          throw new Error(`appVisibility respondeu ${response.status}`);
        }
      } catch {
        // Sinal é best-effort: libera o estado para que a próxima transição possa reenviá-lo.
        lastSentVisibilityRef.current = null;
      }
    },
    [appVisibilityEndpoint, authorizedFetch, userCheckInPayload]
  );

  // Heartbeat da sessão (ver useSessionPoll). Sem keepalive e sem signal: é uma requisição
  // curta, periódica, e uma falha só adia a próxima batida.
  const sendSessionPoll = useCallback(async (): Promise<SessionPollResult> => {
    if (!userCheckInPayload) {
      throw new Error("sessionPoll sem identidade");
    }
    const response = await authorizedFetch(
      sessionPollEndpoint,
      { method: "POST" },
      JSON.stringify({
        tenantId: userCheckInPayload.tenantId,
        userId: userCheckInPayload.userId,
        sessionId,
        appVersion: getSessionIdentity().appVersion,
      })
    );
    if (!response.ok) {
      throw new Error(`sessionPoll respondeu ${response.status}`);
    }
    return (await response.json()) as SessionPollResult;
  }, [authorizedFetch, sessionPollEndpoint, userCheckInPayload]);

  // O sinal de saída da aplicação é emitido pelo par app.lifecycle registrado em
  // useTeamsAppSuspension, e não mais pelo teamsCore.registerBeforeUnloadHandler: além de
  // deprecado, o slot dele no dispatcher do TeamsJS perde para o beforeSuspendOrTerminate, então
  // manter os dois seria código morto com dois donos do mesmo sinal.
  //
  // O par de ciclo de vida passa a ser a fonte autoritativa de visibilidade: com a aplicação
  // suspensa em vez de destruída, o mount deixa de ser o sinal de "voltou a ficar visível".

  return useMemo(
    () => ({
      // Exposto para a tela de administração do tenant (tenantAdminApi), que precisa do mesmo
      // envelope de autenticação do resto: token SSO no Authorization e uma repetição com token
      // novo no 401. Os consumidores anteriores viviam todos dentro deste hook.
      authorizedFetch,
      refresh,
      registrationBackendMessage,
      registrationDetail,
      registrationErrorCode,
      registrationStatus,
      sendAppEventNotification,
      sendAppVisibility,
      sendSessionPoll,
      softphoneConfig,
      tenantAdminAccess,
      teamsHostDiagnostics,
      teamsSsoState,
      teamsUser,
    }),
    [
      authorizedFetch,
      refresh,
      registrationBackendMessage,
      registrationDetail,
      registrationErrorCode,
      registrationStatus,
      sendAppEventNotification,
      sendAppVisibility,
      sendSessionPoll,
      softphoneConfig,
      tenantAdminAccess,
      teamsHostDiagnostics,
      teamsSsoState,
      teamsUser,
    ]
  );
}
